import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import client from '../../api/Client';
import { LockClosedIcon, ShieldCheckIcon, ExclamationCircleIcon, CheckCircleIcon } from '@heroicons/react/24/solid';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';

const ForcePasswordResetModal = () => {
    const { user, loadUser, logout, isAuthenticated } = useAuth();
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);

    if (!isAuthenticated || !user?.requires_password_change) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        
        if (password.length < 6) {
            setError('Password must be at least 6 characters long.');
            return;
        }

        if (password !== confirmPassword) {
            setError('Passwords do not match.');
            return;
        }

        setLoading(true);
        try {
            await client.post('/api/auth/change-password/', { password });
            setSuccess(true);
            setTimeout(async () => {
                await loadUser(); // Refresh user state to clear the flag
            }, 2000);
        } catch (err) {
            console.error('Password reset error:', err);
            setError(err.response?.data?.error || 'Failed to update password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-md px-4">
            <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-xl">
                {/* Decorative background blobs */}
                <div className="absolute -left-20 -top-20 h-40 w-40 rounded-full bg-purple-500/20 blur-3xl"></div>
                <div className="absolute -bottom-20 -right-20 h-40 w-40 rounded-full bg-blue-500/20 blur-3xl"></div>

                <div className="relative text-center">
                    <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg ring-4 ring-white/10">
                        {success ? (
                            <ShieldCheckIcon className="h-8 w-8 text-white animate-bounce" />
                        ) : (
                            <LockClosedIcon className="h-8 w-8 text-white" />
                        )}
                    </div>

                    <h2 className="mb-2 text-3xl font-bold tracking-tight text-white">
                        {success ? "Success!" : "Welcome back!"}
                    </h2>
                    <p className="mb-8 text-indigo-100/70">
                        {success 
                            ? "Your security is our priority. Your password has been updated successfully." 
                            : "For your security, we require a mandatory password update on your first login."}
                    </p>

                    {success ? (
                        <div className="flex flex-col items-center justify-center py-6">
                            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-green-500/20 mb-4">
                                <CheckCircleIcon className="h-8 w-8 text-green-400" />
                            </div>
                            <p className="text-sm font-medium text-green-400">Redirecting to your dashboard...</p>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-5 text-left">
                            <div className="space-y-2">
                                <label className="text-sm font-medium text-indigo-100 ml-1">New Password</label>
                                <div className="group relative">
                                    <input
                                        type={showPassword ? "text" : "password"}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-3 pr-12 text-white placeholder-white/30 outline-none ring-indigo-500/50 transition-all focus:border-indigo-500 focus:ring-4"
                                        placeholder="••••••••"
                                        required
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPassword(!showPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-indigo-200/50 hover:text-indigo-200 transition-colors"
                                        tabIndex="-1"
                                    >
                                        {showPassword ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-indigo-100 ml-1">Confirm New Password</label>
                                <div className="group relative">
                                    <input
                                        type={showConfirmPassword ? "text" : "password"}
                                        value={confirmPassword}
                                        onChange={(e) => setConfirmPassword(e.target.value)}
                                        className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-3 pr-12 text-white placeholder-white/30 outline-none ring-indigo-500/50 transition-all focus:border-indigo-500 focus:ring-4"
                                        placeholder="••••••••"
                                        required
                                        disabled={loading}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-indigo-200/50 hover:text-indigo-200 transition-colors"
                                        tabIndex="-1"
                                    >
                                        {showConfirmPassword ? (
                                            <EyeSlashIcon className="h-5 w-5" />
                                        ) : (
                                            <EyeIcon className="h-5 w-5" />
                                        )}
                                    </button>
                                </div>
                            </div>

                            {error && (
                                <div className="flex items-center gap-2 rounded-xl bg-red-500/10 p-3 text-xs font-medium text-red-400 border border-red-500/20 animate-shake">
                                    <ExclamationCircleIcon className="h-4 w-4 shrink-0" />
                                    <span>{error}</span>
                                </div>
                            )}

                            <button
                                type="submit"
                                disabled={loading}
                                className="group relative w-full overflow-hidden rounded-xl bg-gradient-to-r from-indigo-600 to-purple-600 px-4 py-3.5 font-semibold text-white shadow-xl transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50"
                            >
                                <div className="absolute inset-0 bg-white/20 transition-transform -translate-x-full group-hover:translate-x-0"></div>
                                <span className="relative">
                                    {loading ? "Updating Security..." : "Secure My Account"}
                                </span>
                            </button>
                            
                            <button
                                type="button"
                                onClick={logout}
                                className="w-full py-2 text-xs font-medium text-indigo-200/50 hover:text-indigo-200 transition-colors"
                            >
                                Not you? Sign out
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ForcePasswordResetModal;
